import React, { useState, useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { v4 as uuidv4 } from 'uuid';
import axios from 'axios';
import { onAuthStateChanged } from 'firebase/auth';
import { auth as firebaseAuth } from './firebase/config';

// Components
import Navbar from './components/layout/Navbar';
import DialPad from './components/dialer/DialPad';
import CallSummary from './components/dialer/CallSummary';
import Wallet from './components/wallet/Wallet';
import History from './components/history/CallHistory';
import ContactBook from './components/ContactBook';
import Auth from './components/auth/Auth';
import Profile from './components/Profile';
import Support from './components/Support';
import HomePage from './components/HomePage';
import AdminDashboard from './components/admin/AdminDashboard';
import PrivacyPolicy from './components/legal/PrivacyPolicy';
import TermsRedirect from './components/legal/TermsRedirect';
import RefundRedirect from './components/legal/RefundRedirect';
import './App.css';

import ActiveCall from './components/dialer/ActiveCall';
import CallPage from './components/CallPage';
import RechargePage from './components/RechargePage';
import Blog from './components/blog/Blog';
import BlogPost from './components/blog/BlogPost';
import { Device } from '@twilio/voice-sdk';

import { PayPalScriptProvider } from "@paypal/react-paypal-js";

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// Add ngrok-skip-browser-warning header to bypass ngrok free tier interstitial
// axios.defaults.headers.common['ngrok-skip-browser-warning'] = 'true';

const PAYPAL_CLIENT_ID = process.env.REACT_APP_PAYPAL_CLIENT_ID;
console.log('API URL:', API_URL);
console.log('PayPal Client ID:', PAYPAL_CLIENT_ID ? PAYPAL_CLIENT_ID.substring(0, 10) + '...' : 'Missing');
console.log('Cashfree Mode:', process.env.REACT_APP_CASHFREE_MODE);

const MainApp = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [session, setSession] = useState(localStorage.getItem('sessionId') || '');
  const [toast, setToast] = useState('');

  // Call State
  const [callActive, setCallActive] = useState(false);
  const [callStatus, setCallStatus] = useState('idle'); // idle, ringing, connected
  const [callDuration, setCallDuration] = useState(0);
  const [activeNumber, setActiveNumber] = useState('');
  const [isMuted, setIsMuted] = useState(false);
  const [refreshAuth, setRefreshAuth] = useState(0);

  const deviceRef = useRef(null);
  const callRef = useRef(null);
  const timerRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const isAdmin = location.pathname.startsWith('/admin');

  // Initialize Session
  useEffect(() => {
    if (!localStorage.getItem('sessionId')) {
      const newSession = uuidv4();
      localStorage.setItem('sessionId', newSession);
      setSession(newSession);
    }
  }, []);

  // Listen for Auth (Firebase only)
  useEffect(() => {
    let isMounted = true;

    // Safety timer to prevent infinite loading
    const safetyTimer = setTimeout(() => {
      if (isMounted && loading) setLoading(false);
    }, 12000);

    const loadGuest = async (sId) => {
      try {
        const res = await axios.get(`${API_URL}/users/me`, { headers: { 'x-session-id': sId } });
        if (isMounted) setUser({ ...res.data, isGuest: true });
      } catch (err) { console.error('Guest load error', err); }
      finally { if (isMounted) setLoading(false); }
    };

    const syncUser = async (authData) => {
      try {
        const res = await axios.post(`${API_URL}/users/sync`, {
          ...authData,
          sessionId: session
        });
        if (isMounted) setUser({ ...res.data.user, isGuest: false });
      } catch (err) {
        console.error('Sync error', err);
        await loadGuest(session);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    // Listen to Firebase Auth
    const unsubFirebase = onAuthStateChanged(firebaseAuth, async (fbUser) => {
      if (fbUser) {
        syncUser({
          email: fbUser.email,
          phoneNumber: fbUser.phoneNumber,
          firebaseUid: fbUser.uid
        });
      } else {
        // No Firebase user, check for token or load as guest
        const token = localStorage.getItem('token');
        if (token) {
          try {
            const res = await axios.get(`${API_URL}/users/me`, { headers: { Authorization: `Bearer ${token}` } });
            if (isMounted) setUser({ ...res.data, isGuest: false });
          } catch (e) { loadGuest(session); }
          finally { if (isMounted) setLoading(false); }
        } else {
          loadGuest(session);
        }
      }
    });

    return () => {
      isMounted = false;
      clearTimeout(safetyTimer);
      unsubFirebase();
    };
  }, [session, refreshAuth, loading]);

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(''), 3000);
  };

  const walletBalance = user?.wallet_balance || 0;

  const durationRef = useRef(0); // Ref to track duration without stale closures

  useEffect(() => {
    // Sync state with ref for UI updates if needed, 
    // but primarily we use state for rendering and ref for logic
  }, []);

  const startTimer = () => {
    console.log('[TIMER] Starting timer...');
    setCallDuration(0);
    durationRef.current = 0; // Reset ref

    if (timerRef.current) clearInterval(timerRef.current);

    timerRef.current = setInterval(() => {
      durationRef.current += 1;
      setCallDuration(prev => prev + 1);
      console.log('[TIMER] Tick:', durationRef.current);
    }, 1000);
  };

  const stopTimer = () => {
    console.log('[TIMER] Stopping timer. Final duration (ref):', durationRef.current);
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  const validateNumber = (number) => {
    const cleanNumeric = number.replace(/[^0-9]/g, '');
    const emergencyList = ['911', '112', '999', '111', '000', '100', '101', '102', '108', '110', '119', '120', '15', '17', '18', '190', '192', '193'];
    if (cleanNumeric.length <= 3) return 'Call blocked: Short number.';
    if (emergencyList.includes(cleanNumeric)) return 'Emergency services not supported.';
    return null;
  };

  const formatDuration = (s) => {
    const mins = Math.floor(s / 60);
    const secs = s % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // WebRTC Calling Function
  const handleCall = async (to, method = 'direct', idempotencyKey = null) => {
    if (walletBalance < 1) return showToast('Insufficient funds. Please recharge.');
    const target = to.replace(/[^0-9+]/g, '');
    const error = validateNumber(target);
    if (error) return showToast(error);

    setActiveNumber(target);
    setCallActive(true);
    setCallStatus('ringing');

    try {
      const token = await firebaseAuth.currentUser?.getIdToken() || localStorage.getItem('token');
      const headers = user?.isGuest ? { 'x-session-id': session } : { 'Authorization': `Bearer ${token}` };

      // Build request payload with idempotency key if provided
      const payload = { to: target };
      if (idempotencyKey) {
        payload.idempotencyKey = idempotencyKey;
      }

      if (method === 'bridge') {
        showToast('Initiating bridge call...');
        await axios.post(`${API_URL}/calls/initiate`, payload, { headers });
        setCallStatus('connected');
        startTimer();
      } else {
        // WebRTC Direct Calling
        const tokenRes = await axios.get(`${API_URL}/twilio/token`, { headers });
        if (!deviceRef.current) {
          deviceRef.current = new Device(tokenRes.data.token, { debug: false });
        }
        const call = await deviceRef.current.connect({ params: { To: target } });
        callRef.current = call;
        call.on('accept', () => { setCallStatus('connected'); startTimer(); });
        call.on('disconnect', () => handleEndCall());
        call.on('error', (err) => { showToast(err.message); handleEndCall(); });
      }
    } catch (err) {
      console.error('[CALL ERROR]', err);
      showToast(err.response?.data?.message || 'Call failed');
      setCallActive(false);
      setCallStatus('idle');
      stopTimer();
    }
  };

  const handleEndCall = () => {
    console.log('[CALL] Ending call...');
    stopTimer();

    if (callRef.current) {
      console.log('[CALL] Disconnecting Twilio device...');
      callRef.current.disconnect();
      callRef.current = null;
    }

    setCallActive(false);
    setCallStatus('idle');

    // Use ref value to ensure we get the latest duration even in stale closures
    const finalDur = durationRef.current;
    console.log('[CALL] Final Duration for Summary:', finalDur);

    // Calculate cost: $0.50 per minute, rounded up
    const minutes = Math.ceil(finalDur / 60) || 1;
    const cost = minutes * 0.50;

    navigate('/summary', { state: { duration: formatDuration(finalDur), cost, balance: walletBalance } });

    // Refresh user data after a delay to get the updated balance from server
    setTimeout(async () => {
      try {
        const token = await firebaseAuth.currentUser?.getIdToken() || localStorage.getItem('token');
        const headers = user?.isGuest
          ? { 'x-session-id': session }
          : { 'Authorization': `Bearer ${token}` };
        console.log('[CALL] Fetching updated user balance...');
        const res = await axios.get(`${API_URL}/users/me`, { headers });
        console.log('[CALL] Updated balance:', res.data.wallet_balance);
        setUser(prev => ({ ...prev, wallet_balance: res.data.wallet_balance }));
      } catch (err) {
        console.error('Failed to refresh user balance:', err);
      }
    }, 3000);
  };

  const toggleMute = () => {
    if (callRef.current) {
      const newMute = !isMuted;
      callRef.current.mute(newMute);
      setIsMuted(newMute);
    }
  };

  const handleLogout = async () => {
    await firebaseAuth.signOut();
    localStorage.removeItem('token');
    localStorage.removeItem('sessionId');
    window.location.reload();
  };

  if (loading) return (
    <div style={{ display: 'flex', height: '100vh', alignItems: 'center', justifyContent: 'center', background: '#0f172a' }}>
      <div className="spinner" style={{ width: '40px', height: '40px', border: '4px solid #334155', borderTopColor: '#6366f1', borderRadius: '50%', animation: 'spin 1s linear infinite' }}></div>
    </div>
  );

  if (isAdmin) {
    return (
      <Routes>
        <Route path="/admin" element={<AdminDashboard />} />
        <Route path="/admin/*" element={<Navigate to="/admin" replace />} />
      </Routes>
    );
  }

  const isRechargePage = location.pathname === '/recharge';
  const isBlogPage = location.pathname.startsWith('/blog');

  return (
    <PayPalScriptProvider options={{ "client-id": PAYPAL_CLIENT_ID || "sb", currency: "USD", intent: "capture" }}>
      <div className="app-container">
        {!isRechargePage && <Navbar user={user} onLogout={handleLogout} />}
        <main className={`main-content ${isBlogPage ? 'full-width' : ''}`}>
          {callActive ? (
            <ActiveCall number={activeNumber} status={callStatus} duration={formatDuration(callDuration)} isMuted={isMuted} toggleMute={toggleMute} onEnd={handleEndCall} />
          ) : (
            <Routes>
              <Route path="/" element={<HomePage onCall={handleCall} walletBalance={walletBalance} />} />
              <Route path="/call" element={<DialPad onCall={handleCall} walletBalance={walletBalance} />} />
              <Route path="/wallet" element={<Wallet balance={walletBalance} isGuest={user?.isGuest} user={user} onUpdateUser={setUser} />} />
              <Route path="/history" element={<History user={user} session={session} />} />
              <Route path="/contacts" element={<ContactBook user={user} session={session} />} />
              <Route path="/profile" element={<Profile user={user} onUpdateUser={setUser} />} />
              <Route path="/login" element={<Auth onAuthSuccess={() => { setRefreshAuth(p => p + 1); navigate('/call'); }} />} />
              <Route path="/summary" element={<CallSummary isGuest={user?.isGuest} currentBalance={walletBalance} />} />
              <Route path="/support" element={<Support />} />
              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="/terms" element={<TermsRedirect />} />
              <Route path="/refund" element={<RefundRedirect />} />
              <Route path="/call/:token" element={<CallPage />} />
              <Route path="/recharge" element={<RechargePage />} />
              <Route path="/blog" element={<Blog />} />
              <Route path="/blog/:slug" element={<BlogPost />} />
            </Routes>
          )}
        </main>

        <footer style={{
          textAlign: 'center',
          padding: '1.5rem 1rem',
          borderTop: '1px solid rgba(255,255,255,0.1)',
          marginTop: 'auto',
          color: '#64748b',
          fontSize: '0.875rem'
        }}>
          <div style={{ marginBottom: '0.5rem' }}>
            <a href="/privacy.html#privacy" style={{ color: '#94a3b8', textDecoration: 'none', margin: '0 0.75rem' }}>Privacy Policy</a>
            <span style={{ color: '#475569' }}>•</span>
            <a href="/privacy.html#terms" style={{ color: '#94a3b8', textDecoration: 'none', margin: '0 0.75rem' }}>Terms & Conditions</a>
            <span style={{ color: '#475569' }}>•</span>
            <a href="/privacy.html#refund" style={{ color: '#94a3b8', textDecoration: 'none', margin: '0 0.75rem' }}>Refund Policy</a>
          </div>
          <p style={{ margin: 0 }}>© {new Date().getFullYear()} Ringez. All rights reserved.</p>
        </footer>

        {toast && <div className="toast">{toast}</div>}
      </div>
    </PayPalScriptProvider>
  );
};

const App = () => (
  <HelmetProvider>
    <Router>
      <MainApp />
    </Router>
  </HelmetProvider>
);

export default App;
